import { Link, useLocation } from "react-router-dom";
import { ArrowLeft, LogIn, User, Settings } from "lucide-react";
import { useEffect, useState } from "react";
import { useAppAuth } from "@/components/auth/AuthProvider";
import styles from "./Header.module.scss";

interface HeaderProps {}

export function Header({}: HeaderProps) {
  const location = useLocation();
  const { isSignedIn, provider } = useAppAuth();
  const isMonitor = ["/app", "/settings", "/support"].includes(
    location.pathname,
  );
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 4);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Hide header on auth pages for cleaner look
  const isAuthPage =
    location.pathname.startsWith("/login") ||
    location.pathname.startsWith("/signup");

  if (isAuthPage) {
    return null;
  }

  return (
    <header
      className={`${styles.header} ${scrolled ? styles.scrolled : ""} ${isMonitor ? styles.monitorHeader : ""}`}
    >
      <div className={styles.container}>
        {/* Logo and Mode Badge */}
        <div className={styles.logoGroup}>
          <Link to="/" className={styles.logo}>
            <img
              src="/logo.svg"
              alt="GoodBaby Logo"
              className={styles.logoIcon}
            />
            <span className={styles.logoText}>GoodBaby</span>
          </Link>
        </div>

        {/* Navigation */}
        <nav className={styles.nav}>
          {provider === "development" && !isMonitor ? (
            <DevNavigation />
          ) : (
            <AuthNavigation
              isSignedIn={isSignedIn || provider === "development"}
            />
          )}
        </nav>
      </div>
    </header>
  );
}

/**
 * Navigation for an authenticated user.
 */
function AuthNavigation({ isSignedIn }: { isSignedIn: boolean }) {
  const location = useLocation();
  const isBack = ["/settings", "/support"].includes(location.pathname);
  return (
    <>
      {isSignedIn ? (
        <Link
          to={isBack ? "/app" : "/settings"}
          className={`${styles.navLink} ${
            isBack ? styles.backBtn : styles.settingsBtn
          }`}
        >
          {isBack ? (
            <ArrowLeft size={20} strokeWidth={2} />
          ) : (
            <Settings size={20} strokeWidth={2} />
          )}
          <span>{isBack ? "Back to monitor" : "Settings"}</span>
        </Link>
      ) : (
        <Link to="/login" className={styles.signInButton}>
          <LogIn size={18} strokeWidth={2} />
          <span>Sign In</span>
        </Link>
      )}
    </>
  );
}

/**
 * Navigation used by the development auth fallback.
 */
function DevNavigation() {
  return (
    <div className={styles.devBadge}>
      <User size={16} strokeWidth={2} />
      <span>Dev Mode</span>
    </div>
  );
}
